import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Smartphone, Tablet, Monitor, ShieldCheck, ShieldOff, RotateCcw, Trash2, AlertCircle } from "lucide-react";
import { useI18n } from "../../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function iconFor(label) {
  const l = (label || "").toLowerCase();
  if (l.startsWith("iphone") || l.startsWith("android")) return <Smartphone size={18} />;
  if (l.startsWith("ipad")) return <Tablet size={18} />;
  return <Monitor size={18} />;
}

function shortDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-PE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch (_) { return iso; }
}

export default function MyDevicesPanel({ currentEndpointHash, onReactivate, busy }) {
  const { t } = useI18n();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState({});
  const [confirmAll, setConfirmAll] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/push/my-devices`);
      setDevices(r.data?.devices || []);
    } catch (e) {
      toast.error(t("my_devices.load_error"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { refresh(); }, [refresh]);

  const revokeOne = async (d) => {
    setRevoking((s) => ({ ...s, [d.id]: true }));
    try {
      await axios.post(`${API}/push/revoke-device`, { id: d.id });
      toast.success(t("my_devices.revoked_toast", { name: d.device_label }));
      await refresh();
    } catch (e) {
      toast.error(t("my_devices.revoke_error"));
    } finally {
      setRevoking((s) => ({ ...s, [d.id]: false }));
    }
  };

  const revokeAll = async () => {
    setConfirmAll(false);
    let endpointHashBefore = currentEndpointHash || null;
    let hadLocalSub = false;
    try {
      // 1) Tear down the local browser PushSubscription FIRST so Safari/Chrome
      //    don't keep returning the cached sub on the next "Activar push" tap.
      try {
        if ("serviceWorker" in navigator) {
          const reg = await navigator.serviceWorker.getRegistration();
          if (reg) {
            const localSub = await reg.pushManager.getSubscription();
            if (localSub) {
              hadLocalSub = true;
              try {
                const j = localSub.toJSON?.();
                if (j?.endpoint) {
                  // SHA-256(endpoint).hexdigest()[:16] — purely observability,
                  // we only send the hash to the backend for the bracket log.
                  const enc = new TextEncoder().encode(j.endpoint);
                  const buf = await crypto.subtle.digest("SHA-256", enc);
                  const hex = Array.from(new Uint8Array(buf))
                    .map((b) => b.toString(16).padStart(2, "0")).join("").slice(0, 16);
                  endpointHashBefore = endpointHashBefore || hex;
                }
              } catch (_) { /* ignore — log endpoint_hash optional */ }
              try { await localSub.unsubscribe(); } catch (_) { /* best-effort */ }
            }
          }
        }
      } catch (_) { /* never block backend revoke if local cleanup throws */ }

      // 2) Tell the backend: emit [PUSH_LOCAL_UNSUBSCRIBED] for audit.
      try {
        await axios.post(`${API}/push/local-unsubscribed`, {
          endpoint_hash: endpointHashBefore,
          had_local_sub: hadLocalSub,
          trigger: "revoke_all_button",
        });
      } catch (_) { /* observability only */ }

      // 3) Disable every DB row for this owner.
      const r = await axios.post(`${API}/push/revoke-all-devices`);
      toast.success(t("my_devices.revoked_n", { n: r.data?.revoked ?? 0 }));
      await refresh();
    } catch (e) {
      toast.error(t("my_devices.revoke_all_error"));
    }
  };

  const reactivate = async () => {
    if (!onReactivate) return;
    await onReactivate();
    await refresh();
  };

  const activeCount = devices.filter((d) => d.enabled).length;

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="my-devices-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-white">{t("my_devices.title", { a: activeCount, n: devices.length })}</h3>
        <button
          type="button"
          onClick={refresh}
          disabled={loading}
          className="text-xs text-white/60 hover:text-white"
          data-testid="my-devices-refresh"
        >
          {t("my_devices.refresh")}
        </button>
      </div>

      <p className="text-[11px] text-white/50 mb-3">
        {t("my_devices.desc")}
      </p>

      {loading && <div className="text-xs text-white/50">{t("my_devices.loading")}</div>}

      {!loading && devices.length === 0 && (
        <div className="text-xs text-white/60 italic" data-testid="my-devices-empty">
          {t("my_devices.empty")}
        </div>
      )}

      {!loading && devices.length > 0 && (
        <ul className="space-y-2">
          {devices.map((d) => {
            const isCurrent = currentEndpointHash && d.endpoint_hash === currentEndpointHash;
            return (
              <li
                key={d.id}
                className={`flex items-start gap-3 rounded-md border p-3 ${d.enabled ? "border-emerald-500/30 bg-emerald-500/5" : "border-white/10 bg-white/[0.02] opacity-60"}`}
                data-testid={`device-row-${d.id}`}
              >
                <div className="mt-0.5 text-white/70">{iconFor(d.device_label)}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-white font-medium" data-testid="device-label">
                      {d.device_label}
                    </span>
                    {isCurrent && (
                      <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-blue-500/30 text-blue-700" data-testid="device-current-badge">
                        {t("my_devices.this_device")}
                      </span>
                    )}
                    {d.enabled ? (
                      <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-700">
                        <ShieldCheck size={10} className="inline mr-1" />{t("my_devices.active")}
                      </span>
                    ) : (
                      <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-white/10 text-white/50">
                        <ShieldOff size={10} className="inline mr-1" />{t("my_devices.revoked")}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-white/50 mt-1 font-mono space-x-2">
                    <span>id: {d.endpoint_hash || d.id.slice(0, 8)}</span>
                    <span>·</span>
                    <span>{t("my_devices.last_seen")}: {shortDate(d.last_seen_at)}</span>
                  </div>
                  {d.failure_reason && !d.enabled && (
                    <div className="text-[10px] text-amber-700/80 mt-1" data-testid="device-failure-reason">
                      <AlertCircle size={10} className="inline mr-0.5" />
                      {d.failure_reason}
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1">
                  {isCurrent && !d.enabled && (
                    <button
                      type="button"
                      onClick={reactivate}
                      disabled={busy}
                      className="text-[11px] px-2 py-1 rounded bg-blue-500/30 text-blue-700 hover:bg-blue-500/50 disabled:opacity-50"
                      data-testid="device-reactivate-btn"
                    >
                      <RotateCcw size={11} className="inline mr-1" />{t("my_devices.reactivate")}
                    </button>
                  )}
                  {d.enabled && (
                    <button
                      type="button"
                      onClick={() => revokeOne(d)}
                      disabled={!!revoking[d.id]}
                      className="text-[11px] px-2 py-1 rounded bg-red-500/20 text-red-700 hover:bg-red-500/40 disabled:opacity-50"
                      data-testid={`device-revoke-btn-${d.id}`}
                    >
                      <Trash2 size={11} className="inline mr-1" />{t("my_devices.revoke")}
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {!loading && activeCount > 0 && (
        <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between">
          {!confirmAll ? (
            <button
              type="button"
              onClick={() => setConfirmAll(true)}
              className="text-[11px] px-3 py-1.5 rounded bg-red-500/10 text-red-700 hover:bg-red-500/20 border border-red-500/30"
              data-testid="device-revoke-all-btn"
            >
              <Trash2 size={11} className="inline mr-1" />{t("my_devices.revoke_all")}
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-amber-700">{t("my_devices.revoke_all_confirm")}</span>
              <button
                type="button"
                onClick={revokeAll}
                className="text-[11px] px-2 py-1 rounded bg-red-500 text-white hover:bg-red-600"
                data-testid="device-revoke-all-confirm-btn"
              >
                {t("my_devices.yes_revoke")}
              </button>
              <button
                type="button"
                onClick={() => setConfirmAll(false)}
                className="text-[11px] px-2 py-1 rounded bg-white/10 text-white/70"
                data-testid="device-revoke-all-cancel-btn"
              >
                {t("my_devices.cancel")}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
